import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  XCircle,
  MinusCircle,
  Clock,
  RotateCcw,
  Timer,
  BookOpen,
} from "lucide-react";
import StudyShell from "@/components/study/StudyShell";
import ProtectedContent from "@/components/study/ProtectedContent";
import QuestionReportButton from "@/components/study/QuestionReportButton";
import { getSprintResults, trackEvent } from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const SECTION_LABELS = { state: "Illinois State", national: "National", math: "Math" };

function fmtClock(secs) {
  const s = Math.max(0, Math.floor(secs || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${String(r).padStart(2, "0")}s`;
}

const Stat = ({ label, value, tone }) => (
  <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-4 text-center">
    <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
      {label}
    </p>
    <p className={`mt-1 text-2xl font-bold ${tone || "text-[var(--ibp-ink)]"}`}>{value}</p>
  </div>
);

export default function StudySprintResults() {
  const { attemptId } = useParams();
  const [showReview, setShowReview] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["sprint-results", attemptId],
    queryFn: () => getSprintResults(attemptId),
  });

  if (isLoading) {
    return (
      <StudyShell testId={STUDY.sprintResults}>
        <p className="py-10 text-center text-sm text-[color:rgba(11,18,32,0.6)]">Loading results...</p>
      </StudyShell>
    );
  }

  if (isError || !data) {
    return (
      <StudyShell testId={STUDY.sprintResults}>
        <div className="rounded-2xl border border-dashed border-[color:rgba(10,27,51,0.2)] bg-white px-6 py-14 text-center">
          <p className="text-base font-semibold text-[var(--ibp-ink)]">Results unavailable.</p>
          <Link
            to="/study/mini-exams"
            className="mt-4 inline-flex rounded-xl bg-[var(--ibp-navy-950)] px-5 py-2.5 text-sm font-semibold text-white"
          >
            Back to Exam Mode
          </Link>
        </div>
      </StudyShell>
    );
  }

  const review = data.review || [];
  const missed = review.filter((r) => r.status === "incorrect" || r.status === "unanswered");
  const sections = data.section_breakdown || {};
  const topics = data.topic_breakdown || {};
  const isSimulation = data.kind === "simulation";
  const isMarathon = data.kind === "marathon";
  const resultsLabel = isSimulation
    ? "Exam Simulation - Results"
    : isMarathon
    ? "Combined Marathon - Results"
    : "Timed Review Sprint - Results";

  const onToggleReview = () => {
    const next = !showReview;
    setShowReview(next);
    if (next) {
      trackEvent("timed_sprint_review_started", {
        attempt_id: data.attempt_id,
        sprint_type: data.sprint_type,
      });
    }
  };

  return (
    <StudyShell testId={STUDY.sprintResults}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
            {resultsLabel}
          </p>
          <h1 className="text-2xl font-bold text-[var(--ibp-ink)]">{data.sprint_title}</h1>
        </div>
        {data.auto_submitted ? (
          <span className="rounded-full bg-[color:rgba(155,44,44,0.1)] px-3 py-1 text-xs font-semibold text-[#9b2c2c]">
            Auto-submitted (time expired)
          </span>
        ) : null}
      </div>

      {/* Score headline */}
      <div className="mt-5 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 text-center shadow-[0_10px_30px_rgba(7,20,39,0.05)]">
        <p className="text-sm font-semibold text-[color:rgba(11,18,32,0.6)]">Your score</p>
        <p className="mt-1 text-5xl font-bold text-[var(--ibp-ink)]">{data.percent_score}%</p>
        <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.7)]">
          {data.score} of {data.total_count} correct
        </p>
        {data.passing_score ? (
          <span
            className={[
              "mt-4 inline-flex rounded-full px-3 py-1.5 text-xs font-semibold",
              data.practice_target_met
                ? "bg-[color:rgba(21,128,61,0.12)] text-[#15803d]"
                : "bg-[color:rgba(214,162,30,0.14)] text-[#7a5a12]",
            ].join(" ")}
          >
            {data.practice_target_met ? "Practice target met" : "Keep building"} · {data.passing_score}% target
          </span>
        ) : null}
      </div>

      {/* Count stats */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Correct" value={data.correct_count} tone="text-[#15803d]" />
        <Stat label="Incorrect" value={data.incorrect_count} tone="text-[#9b2c2c]" />
        <Stat label="Unanswered" value={data.unanswered_count} tone="text-[color:rgba(11,18,32,0.6)]" />
        <Stat label="Time used" value={fmtClock(data.time_used_seconds)} />
      </div>

      {/* Section breakdown */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5">
          <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Section breakdown</h2>
          <ul className="mt-3 space-y-2">
            {Object.keys(sections).length === 0 ? (
              <li className="text-sm text-[color:rgba(11,18,32,0.6)]">No sections.</li>
            ) : (
              Object.entries(sections).map(([p, s]) => (
                <li key={p} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-[var(--ibp-ink)]">
                    {SECTION_LABELS[p] || p}
                  </span>
                  <span className="font-semibold text-[var(--ibp-ink)]">
                    {s.correct}/{s.total}
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>

        <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5">
          <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Topic breakdown</h2>
          <ul className="mt-3 max-h-48 space-y-2 overflow-auto">
            {Object.entries(topics).map(([t, s]) => (
              <li key={t} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-[var(--ibp-ink)]">{t}</span>
                <span className="font-semibold text-[var(--ibp-ink)]">
                  {s.correct}/{s.total}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Recommended + weakest */}
      <div className="mt-6 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6">
        <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Recommended next action</h2>
        <p className="mt-1.5 text-sm text-[color:rgba(11,18,32,0.75)]">{data.recommended_action}</p>
        {data.weakest_topics && data.weakest_topics.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
              Weakest topics
            </p>
            <ul className="mt-2 space-y-1.5">
              {data.weakest_topics.map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-[var(--ibp-ink)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--ibp-gold-500)]" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          data-testid={STUDY.sprintReviewToggle}
          onClick={onToggleReview}
          className="inline-flex items-center gap-2 rounded-xl bg-[var(--ibp-navy-950)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
        >
          <BookOpen className="h-4 w-4" /> {showReview ? "Hide explanations" : "Review explanations"}
        </button>
        <Link
          to="/study/missed"
          className="inline-flex items-center gap-2 rounded-xl border border-[color:rgba(10,27,51,0.2)] px-5 py-2.5 text-sm font-semibold text-[var(--ibp-ink)]"
        >
          <RotateCcw className="h-4 w-4" /> Redo missed questions
        </Link>
        <Link
          to="/study/mini-exams"
          className="inline-flex items-center gap-2 rounded-xl border border-[color:rgba(10,27,51,0.2)] px-5 py-2.5 text-sm font-semibold text-[var(--ibp-ink)]"
        >
          <Timer className="h-4 w-4" /> Back to Exam Mode
        </Link>
      </div>

      <p className="mt-3 text-xs text-[color:rgba(11,18,32,0.55)]">
        Your missed and unanswered questions were added to Mistake Review. They clear only after you
        answer them correctly twice there.
      </p>

      {/* Missed list */}
      {missed.length > 0 && (
        <ProtectedContent className="mt-6">
          <h2 className="text-base font-semibold text-[var(--ibp-ink)]">
            Missed questions ({missed.length})
          </h2>
          <ul className="mt-3 space-y-2">
            {missed.map((r) => (
              <li
                key={r.id}
                className="flex items-start gap-2 rounded-xl border border-[color:rgba(10,27,51,0.12)] bg-white px-4 py-3 text-sm"
              >
                {r.status === "unanswered" ? (
                  <MinusCircle className="mt-0.5 h-4 w-4 flex-none text-[color:rgba(11,18,32,0.5)]" />
                ) : (
                  <XCircle className="mt-0.5 h-4 w-4 flex-none text-[#9b2c2c]" />
                )}
                <span className="text-[var(--ibp-ink)]">{r.question_text}</span>
              </li>
            ))}
          </ul>
        </ProtectedContent>
      )}

      {/* Full review (explanations only after submit) */}
      {showReview && (
        <ProtectedContent className="mt-6 space-y-4">
          <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Question-by-question review</h2>
          {review.map((r, i) => (
            <div
              key={r.id}
              className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-[var(--ibp-ink)]">Question {i + 1}</p>
                <span className="flex items-center gap-1.5 text-xs font-semibold">
                  {r.status === "correct" ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-[#15803d]" />
                      <span className="text-[#14532d]">Correct</span>
                    </>
                  ) : r.status === "unanswered" ? (
                    <>
                      <MinusCircle className="h-4 w-4 text-[color:rgba(11,18,32,0.5)]" />
                      <span className="text-[color:rgba(11,18,32,0.6)]">Unanswered</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 text-[#9b2c2c]" />
                      <span className="text-[#7f1d1d]">Incorrect</span>
                    </>
                  )}
                </span>
              </div>
              <p className="mt-2 text-sm font-medium text-[var(--ibp-ink)]">{r.question_text}</p>
              <div className="mt-3 space-y-1.5">
                {["A", "B", "C", "D"].map((letter) => {
                  const isCorrect = r.correct_answer === letter;
                  const isPicked = r.selected_answer === letter;
                  return (
                    <div
                      key={letter}
                      className={[
                        "flex items-start gap-2 rounded-lg border px-3 py-2 text-sm",
                        isCorrect
                          ? "border-[#15803d] bg-[color:rgba(21,128,61,0.08)]"
                          : isPicked
                          ? "border-[#9b2c2c] bg-[color:rgba(155,44,44,0.06)]"
                          : "border-[color:rgba(10,27,51,0.12)]",
                      ].join(" ")}
                    >
                      <span className="font-bold text-[var(--ibp-ink)]">{letter}.</span>
                      <span className="text-[var(--ibp-ink)]">{r.choices[letter]}</span>
                      {isCorrect ? (
                        <span className="ml-auto text-xs font-semibold text-[#15803d]">Correct</span>
                      ) : isPicked ? (
                        <span className="ml-auto text-xs font-semibold text-[#9b2c2c]">Your pick</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 rounded-xl bg-[var(--ibp-offwhite)] p-3">
                <p className="text-sm leading-6 text-[color:rgba(11,18,32,0.8)]">{r.explanation}</p>
                {r.source_reference ? (
                  <p className="mt-2 text-xs text-[color:rgba(11,18,32,0.55)]">
                    Source: {r.source_reference}
                  </p>
                ) : null}
              </div>
              <QuestionReportButton questionId={r.id} />
            </div>
          ))}
        </ProtectedContent>
      )}
    </StudyShell>
  );
}
